import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { requestDefaulAuth } from "../../../../http/httpRequest";
import { Section } from "../../../../components/table/Section";

const fmtBs = (v) =>
  Number(v || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtInt = (v) => Number(v || 0).toLocaleString("es-BO");

const Campo = ({ label, valor, tipo = "bs" }) => {
  const val = Number(valor || 0);
  if (val <= 0) return null;

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="font-semibold text-gray-800 dark:text-gray-200">
        {tipo === "bs" ? `Bs. ${fmtBs(val)}` : fmtInt(val)}
      </span>
    </div>
  );
};

const ProductoCard = ({ producto }) => {
  const tieneVentas = 
    Number(producto.venta_total_bs || 0) > 0 ||
    Number(producto.cantidad_total || 0) > 0 ||
    Number(producto.efectivo_total || 0) > 0 ||
    Number(producto.transferencia_total || 0) > 0 ||
    Number(producto.deuda_total || 0) > 0 ||
    Number(producto.envase_recogido_total || 0) > 0 ||
    Number(producto.envase_deuda_total || 0) > 0 ||
    Number(producto.monto_garantia_total || 0) > 0 ||
    Number(producto.monto_garantia_transferencia_total || 0) > 0 ||
    Number(producto.prestamo_pendiente_total || 0) > 0 ||
    Number(producto.garantia_pendiente_total || 0) > 0;

  const tieneDeudas =
    Number(producto.directa_deuda_total || 0) > 0 ||
    Number(producto.directa_prestamo_cantidad_total || 0) > 0 ||
    Number(producto.directa_garantia_cantidad_total || 0) > 0 ||
    Number(producto.directa_garantia_efectivo_total || 0) > 0 ||
    Number(producto.directa_garantia_transferencia_total || 0) > 0;

  const tieneCobros =
    Number(producto.cobro_deuda_efectivo_total || 0) > 0 ||
    Number(producto.cobro_deuda_transferencia_total || 0) > 0 ||
    Number(producto.recojo_prestamo_cantidad_total || 0) > 0 ||
    Number(producto.recojo_garantia_cantidad_total || 0) > 0 ||
    Number(producto.envase_devuelto_efectivo_total || 0) > 0 ||
    Number(producto.envase_devuelto_transferencia_total || 0) > 0;

  const tieneEnvasesDetalle =
    Number(producto.envases_recogidos_deuda || 0) > 0 ||
    Number(producto.envases_aun_en_deuda || 0) > 0 ||
    Number(producto.envases_recogidos_garantia || 0) > 0 ||
    Number(producto.envases_aun_en_garantia || 0) > 0 ||
    Number(producto.envases_recogidos_deuda_cuaderno || 0) > 0 ||
    Number(producto.envases_recogidos_deuda_directa || 0) > 0 ||
    Number(producto.envases_recogidos_garantia_cuaderno || 0) > 0 ||
    Number(producto.envases_recogidos_garantia_directa || 0) > 0;

  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
      {/* Header del producto */}
      <div className="bg-gray-50 dark:bg-gray-700 p-4 border-b dark:border-gray-600">
        <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">
          {producto.producto_nombre}
        </h3>
        <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-300">
          <div>
            <span className="font-medium">Cantidad:</span>{" "}
            <span className="font-semibold">{fmtInt(producto.cantidad_total)}</span>
          </div>
          <div>
            <span className="font-medium">Total:</span>{" "}
            <span className="font-semibold text-green-600 dark:text-green-400">
              Bs. {fmtBs(producto.venta_total_bs)}
            </span>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* VENTAS */}
          {tieneVentas && (
            <div className="border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
              <div className="bg-blue-50 dark:bg-blue-900/30 px-3 py-2 border-b border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                  VENTAS
                </h4>
              </div>
              <div className="p-3 space-y-1">
                <Campo label="Efectivo" valor={producto.efectivo_total} />
                <Campo label="Transferencia" valor={producto.transferencia_total} />
                <Campo label="A deuda" valor={producto.deuda_total} />
                <Campo label="Envases recogidos" valor={producto.envase_recogido_total} tipo="int" />
                <Campo 
                  label={producto.producto_id === 1 || producto.producto_id === 2 ? "Envases a deuda" : "Productos vendidos"} 
                  valor={producto.envase_deuda_total} 
                  tipo="int" 
                />
                <Campo label="Garantía efectivo" valor={producto.monto_garantia_total} />
                <Campo label="Garantía transferencia" valor={producto.monto_garantia_transferencia_total} />
                <Campo label="Préstamos pendientes" valor={producto.prestamo_pendiente_total} tipo="int" />
                <Campo label="Garantías pendientes" valor={producto.garantia_pendiente_total} tipo="int" />
              </div>
            </div>
          )}

          {/* DEUDAS DIRECTAS */}
          {tieneDeudas && (
            <div className="border border-red-200 dark:border-red-800 rounded-lg overflow-hidden">
              <div className="bg-red-50 dark:bg-red-900/30 px-3 py-2 border-b border-red-200 dark:border-red-800">
                <h4 className="font-semibold text-sm text-red-900 dark:text-red-100">
                  DEUDAS DIRECTAS
                </h4>
              </div>
              <div className="p-3 space-y-1">
                <Campo label="Deuda directa" valor={producto.directa_deuda_total} />
                <Campo label="Préstamo (cantidad)" valor={producto.directa_prestamo_cantidad_total} tipo="int" />
                <Campo label="Garantía (cantidad)" valor={producto.directa_garantia_cantidad_total} tipo="int" />
                <Campo label="Garantía efectivo" valor={producto.directa_garantia_efectivo_total} />
                <Campo label="Garantía transferencia" valor={producto.directa_garantia_transferencia_total} />
              </div>
            </div>
          )}

          {/* COBROS */}
          {tieneCobros && (
            <div className="border border-green-200 dark:border-green-800 rounded-lg overflow-hidden">
              <div className="bg-green-50 dark:bg-green-900/30 px-3 py-2 border-b border-green-200 dark:border-green-800">
                <h4 className="font-semibold text-sm text-green-900 dark:text-green-100">
                  COBROS DE DEUDAS
                </h4>
              </div>
              <div className="p-3 space-y-1">
                <Campo label="Cobro efectivo" valor={producto.cobro_deuda_efectivo_total} />
                <Campo label="Cobro transferencia" valor={producto.cobro_deuda_transferencia_total} />
                <Campo label="Recojo préstamo" valor={producto.recojo_prestamo_cantidad_total} tipo="int" />
                <Campo label="Recojo garantía" valor={producto.recojo_garantia_cantidad_total} tipo="int" />
                <Campo label="Envase devuelto efectivo" valor={producto.envase_devuelto_efectivo_total} />
                <Campo label="Envase devuelto transferencia" valor={producto.envase_devuelto_transferencia_total} />
              </div>
            </div>
          )}
        </div>

        {/* Detalle de envases - Full width debajo */}
        {tieneEnvasesDetalle && (
          <div className="mt-4 border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
            <div className="bg-amber-50 dark:bg-amber-900/30 px-3 py-2 border-b border-amber-200 dark:border-amber-800">
              <h4 className="font-semibold text-sm text-amber-900 dark:text-amber-100">
                DETALLE DE ENVASES
              </h4>
            </div>
            <div className="p-3">
              {/* Fila 1: Estado de envases */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 pb-3 border-b dark:border-amber-700">
                <Campo label="Recogidos (deuda)" valor={producto.envases_recogidos_deuda} tipo="int" />
                <Campo label="Aún en deuda" valor={producto.envases_aun_en_deuda} tipo="int" />
                <Campo label="Recogidos (garantía)" valor={producto.envases_recogidos_garantia} tipo="int" />
                <Campo label="Aún en garantía" valor={producto.envases_aun_en_garantia} tipo="int" />
              </div>
              
              {/* Fila 2: Origen de recogidos - Deuda */}
              {(Number(producto.envases_recogidos_deuda_cuaderno || 0) > 0 || 
                Number(producto.envases_recogidos_deuda_directa || 0) > 0) && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                    Envases recogidos en deuda por origen:
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Campo label="Desde cuaderno" valor={producto.envases_recogidos_deuda_cuaderno} tipo="int" />
                    <Campo label="Desde directa" valor={producto.envases_recogidos_deuda_directa} tipo="int" />
                  </div>
                </div>
              )}

              {/* Fila 3: Origen de recogidos - Garantía */}
              {(Number(producto.envases_recogidos_garantia_cuaderno || 0) > 0 || 
                Number(producto.envases_recogidos_garantia_directa || 0) > 0) && (
                <div>
                  <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                    Envases recogidos en garantía por origen:
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Campo label="Desde cuaderno" valor={producto.envases_recogidos_garantia_cuaderno} tipo="int" />
                    <Campo label="Desde directa" valor={producto.envases_recogidos_garantia_directa} tipo="int" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const IncentivoDetalleView = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const fallback = "/rol/contabilidad/menu/incentivos";

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }

    navigate(fallback, { replace: true });
  };
  const { userId } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const [load, setLoad] = useState(false);
  const [data, setData] = useState(null);

  const paginate = state?.paginate || { initial: "", final: "" };
  const user = state?.user;

  const fetchDetalle = async () => {
    setLoad(true);

    await requestDefaulAuth(
      "post",
      "/incentivos/detalle",
      data,
      setData,
      dispatch,
      {
        user_id: Number(userId),
        initial: paginate?.initial || "",
        final: paginate?.final || "",
      }
    );

    setLoad(false);
  };

  useEffect(() => {
    fetchDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paginate?.initial, paginate?.final]);

  const resumen = useMemo(() => {
    const rows = data?.porProducto || [];
    const acc = {
      venta_total_bs: 0,
      cantidad_total: 0,
      envase_recogido_total: 0,
      envase_deuda_total: 0,
      efectivo_total: 0,
      transferencia_total: 0,
      deuda_total: 0,
      monto_garantia_total: 0,
      monto_garantia_transferencia_total: 0,
      prestamo_pendiente_total: 0,
      garantia_pendiente_total: 0,
      directa_deuda_total: 0,
      directa_prestamo_cantidad_total: 0,
      directa_garantia_cantidad_total: 0,
      directa_garantia_efectivo_total: 0,
      directa_garantia_transferencia_total: 0,
      cobro_deuda_efectivo_total: 0,
      cobro_deuda_transferencia_total: 0,
      recojo_prestamo_cantidad_total: 0,
      recojo_garantia_cantidad_total: 0,
      envase_devuelto_efectivo_total: 0,
      envase_devuelto_transferencia_total: 0,
      envases_recogidos_deuda_cuaderno: 0,
      envases_recogidos_deuda_directa: 0,
      envases_recogidos_garantia_cuaderno: 0,
      envases_recogidos_garantia_directa: 0,
      envases_recogidos_deuda: 0,
      envases_aun_en_deuda: 0,
      envases_recogidos_garantia: 0,
      envases_aun_en_garantia: 0,
      envases_recogidos_deuda_total: 0,
      envases_recogidos_garantia_total: 0,
    };

    for (const r of rows) {
      for (const k of Object.keys(acc)) acc[k] += Number(r?.[k] || 0);
    }

    return acc;
  }, [data]);

  return (
    <Section>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={handleBack}
          className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-700 text-sm"
        >
          ← Atrás
        </button>

        <div className="text-right">
          <div className="text-xs text-gray-400">
            Rango: {data?.initial || paginate.initial || "—"} a {data?.final || paginate.final || "—"}
          </div>
        </div>
      </div>

      <div className="text-center mb-4">
        <div className="text-xl font-bold text-gray-700 dark:text-gray-200">
          DETALLE DE INCENTIVOS
        </div>
        <div className="text-gray-500 dark:text-gray-400">
          {user?.nombre} — {user?.rolName}
        </div>
      </div>

      {load ? (
        <div className="p-4">Cargando…</div>
      ) : (
        <>
          {/* Resumen principal */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-4 shadow-md">
              <div className="text-xs opacity-90 mb-1">Venta Total</div>
              <div className="text-2xl font-bold">Bs. {fmtBs(resumen.venta_total_bs)}</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-4 shadow-md">
              <div className="text-xs opacity-90 mb-1">Cantidad Vendida</div>
              <div className="text-2xl font-bold">{fmtInt(resumen.cantidad_total)}</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg p-4 shadow-md">
              <div className="text-xs opacity-90 mb-1">Total Envases Recogidos</div>
              <div className="text-2xl font-bold">
                {fmtInt(resumen.envase_recogido_total + resumen.envases_recogidos_deuda + resumen.envases_recogidos_garantia)}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-4 shadow-md">
              <div className="text-xs opacity-90 mb-1">Productos</div>
              <div className="text-2xl font-bold">{fmtInt(data?.porProducto?.length || 0)}</div>
            </div>
          </div>

          {/* Resumen por categoría */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Resumen Ventas */}
            <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg overflow-hidden">
              <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-2 border-b border-blue-200 dark:border-blue-700">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">VENTAS</h3>
              </div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.efectivo_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.transferencia_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">A deuda:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.deuda_total)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Env. recogidos:</span>
                  <span className="font-semibold">{fmtInt(resumen.envase_recogido_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Préstamos pend.:</span>
                  <span className="font-semibold">{fmtInt(resumen.prestamo_pendiente_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Garantías pend.:</span>
                  <span className="font-semibold">{fmtInt(resumen.garantia_pendiente_total)}</span>
                </div>
              </div>
            </div>

            {/* Resumen Deudas */}
            <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg overflow-hidden">
              <div className="bg-red-50 dark:bg-red-900/30 px-4 py-2 border-b border-red-200 dark:border-red-700">
                <h3 className="font-semibold text-red-900 dark:text-red-100">DEUDAS DIRECTAS</h3>
              </div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Deuda directa:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.directa_deuda_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Préstamos:</span>
                  <span className="font-semibold">{fmtInt(resumen.directa_prestamo_cantidad_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Garantías:</span>
                  <span className="font-semibold">{fmtInt(resumen.directa_garantia_cantidad_total)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Garantía efectivo:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.directa_garantia_efectivo_total)}</span>
                </div>
              </div>
            </div>

            {/* Resumen Cobros */}
            <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-lg overflow-hidden">
              <div className="bg-green-50 dark:bg-green-900/30 px-4 py-2 border-b border-green-200 dark:border-green-700">
                <h3 className="font-semibold text-green-900 dark:text-green-100">COBROS</h3>
              </div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.cobro_deuda_efectivo_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.cobro_deuda_transferencia_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Recojo préstamo:</span>
                  <span className="font-semibold">{fmtInt(resumen.recojo_prestamo_cantidad_total)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Env. devuelto (Ef):</span>
                  <span className="font-semibold">Bs. {fmtBs(resumen.envase_devuelto_efectivo_total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen de envases - Full width */}
          {(Number(resumen.envases_recogidos_deuda || 0) > 0 ||
            Number(resumen.envases_aun_en_deuda || 0) > 0 ||
            Number(resumen.envases_recogidos_garantia || 0) > 0 ||
            Number(resumen.envases_aun_en_garantia || 0) > 0 ||
            Number(resumen.envases_recogidos_deuda_cuaderno || 0) > 0 ||
            Number(resumen.envases_recogidos_deuda_directa || 0) > 0 ||
            Number(resumen.envases_recogidos_garantia_cuaderno || 0) > 0 ||
            Number(resumen.envases_recogidos_garantia_directa || 0) > 0) && (
            <div className="bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-lg overflow-hidden mb-6">
              <div className="bg-amber-50 dark:bg-amber-900/30 px-4 py-2 border-b border-amber-200 dark:border-amber-700">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100">DETALLE DE ENVASES</h3>
              </div>
              <div className="p-4">
                {/* Estado general */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pb-4 border-b dark:border-amber-700">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Recogidos (deuda):</span>
                    <span className="font-semibold">{fmtInt(resumen.envases_recogidos_deuda)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Aún en deuda:</span>
                    <span className="font-semibold">{fmtInt(resumen.envases_aun_en_deuda)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Recogidos (garantía):</span>
                    <span className="font-semibold">{fmtInt(resumen.envases_recogidos_garantia)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Aún en garantía:</span>
                    <span className="font-semibold">{fmtInt(resumen.envases_aun_en_garantia)}</span>
                  </div>
                </div>

                {/* Origen de recogidos */}
                <div className="text-sm">
                  {(Number(resumen.envases_recogidos_deuda_cuaderno || 0) > 0 || 
                    Number(resumen.envases_recogidos_deuda_directa || 0) > 0) && (
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                        Envases recogidos en deuda por origen:
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Number(resumen.envases_recogidos_deuda_cuaderno || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Desde cuaderno:</span>
                            <span className="font-semibold">{fmtInt(resumen.envases_recogidos_deuda_cuaderno)}</span>
                          </div>
                        )}
                        {Number(resumen.envases_recogidos_deuda_directa || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Desde directa:</span>
                            <span className="font-semibold">{fmtInt(resumen.envases_recogidos_deuda_directa)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(Number(resumen.envases_recogidos_garantia_cuaderno || 0) > 0 || 
                    Number(resumen.envases_recogidos_garantia_directa || 0) > 0) && (
                    <div>
                      <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                        Envases recogidos en garantía por origen:
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Number(resumen.envases_recogidos_garantia_cuaderno || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Desde cuaderno:</span>
                            <span className="font-semibold">{fmtInt(resumen.envases_recogidos_garantia_cuaderno)}</span>
                          </div>
                        )}
                        {Number(resumen.envases_recogidos_garantia_directa || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Desde directa:</span>
                            <span className="font-semibold">{fmtInt(resumen.envases_recogidos_garantia_directa)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Título de sección */}
          <div className="mb-4">
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">
              Detalle por Producto
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Solo se muestran campos con valores
            </p>
          </div>

          {/* Lista de productos */}
          {data?.porProducto?.length ? (
            <div className="space-y-4">
              {data.porProducto.map((producto) => (
                <ProductoCard
                  key={`${producto.producto_id ?? ""}-${producto.almacen_producto_id ?? ""}-${producto.producto_nombre}`}
                  producto={producto}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center text-gray-400">
              Sin datos en el rango seleccionado.
            </div>
          )}

          {/* Ventas por tipo de cliente */}
          {data?.ventasPorTipoCliente && (
            <div className="mt-8 space-y-6">
              {/* EMPRESAS */}
              {data.ventasPorTipoCliente.Empresa?.length > 0 && (
                <div>
                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">
                      Ventas a Empresas
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {data.ventasPorTipoCliente.Empresa.map((cliente) => (
                      <div
                        key={cliente.cliente_id}
                        className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Header del cliente */}
                        <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 border-b dark:border-gray-700">
                          <div className="flex items-start justify-between flex-wrap gap-4">
                            <div>
                              <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                                {cliente.cliente_nombre}
                              </h3>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {cliente.tipo_negocio}
                              </span>
                            </div>
                            <div className="flex gap-4 text-sm">
                              <div className="text-right">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Venta Total</div>
                                <div className="font-bold text-gray-800 dark:text-gray-200">
                                  Bs. {fmtBs(cliente.total_venta_bs)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Cobrado</div>
                                <div className="font-bold text-green-600 dark:text-green-400">
                                  Bs. {fmtBs(cliente.total_cobrado_bs)}
                                </div>
                              </div>
                              {Number(cliente.total_deuda_bs || 0) > 0 && (
                                <div className="text-right">
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Deuda</div>
                                  <div className="font-bold text-red-600 dark:text-red-400">
                                    Bs. {fmtBs(cliente.total_deuda_bs)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Productos del cliente */}
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cliente.productos.map((prod) => (
                              <div
                                key={prod.producto_id}
                                className="border dark:border-gray-700 rounded-lg p-3"
                              >
                                <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                  {prod.producto_nombre}
                                </div>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Cantidad:</span>
                                    <span className="font-semibold">{fmtInt(prod.cantidad_total)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Venta:</span>
                                    <span className="font-semibold">Bs. {fmtBs(prod.venta_total_bs)}</span>
                                  </div>
                                  {Number(prod.efectivo_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                                      <span className="font-semibold">Bs. {fmtBs(prod.efectivo_total_bs)}</span>
                                    </div>
                                  )}
                                  {Number(prod.transferencia_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                                      <span className="font-semibold">Bs. {fmtBs(prod.transferencia_total_bs)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between pt-1 border-t dark:border-gray-700">
                                    <span className="text-gray-600 dark:text-gray-400">Cobrado:</span>
                                    <span className="font-semibold text-green-600 dark:text-green-400">
                                      Bs. {fmtBs(prod.cobrado_total_bs)}
                                    </span>
                                  </div>
                                  {Number(prod.deuda_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Deuda:</span>
                                      <span className="font-semibold text-red-600 dark:text-red-400">
                                        Bs. {fmtBs(prod.deuda_total_bs)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Totales del cliente */}
                          <div className="mt-3 pt-3 border-t dark:border-gray-700 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Cantidad total:</span>
                              <div className="font-semibold">{fmtInt(cliente.total_cantidad)}</div>
                            </div>
                            {Number(cliente.total_efectivo_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                                <div className="font-semibold">Bs. {fmtBs(cliente.total_efectivo_bs)}</div>
                              </div>
                            )}
                            {Number(cliente.total_transferencia_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                                <div className="font-semibold">Bs. {fmtBs(cliente.total_transferencia_bs)}</div>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Cobrado:</span>
                              <div className="font-semibold text-green-600 dark:text-green-400">
                                Bs. {fmtBs(cliente.total_cobrado_bs)}
                              </div>
                            </div>
                            {Number(cliente.total_deuda_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Deuda:</span>
                                <div className="font-semibold text-red-600 dark:text-red-400">
                                  Bs. {fmtBs(cliente.total_deuda_bs)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MAYORISTAS */}
              {data.ventasPorTipoCliente.Mayorista?.length > 0 && (
                <div>
                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">
                      Ventas a Mayoristas
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {data.ventasPorTipoCliente.Mayorista.map((cliente) => (
                      <div
                        key={cliente.cliente_id}
                        className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Header del cliente */}
                        <div className="bg-orange-50 dark:bg-orange-900/30 p-4 border-b dark:border-gray-700">
                          <div className="flex items-start justify-between flex-wrap gap-4">
                            <div>
                              <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                                {cliente.cliente_nombre}
                              </h3>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {cliente.tipo_negocio}
                              </span>
                            </div>
                            <div className="flex gap-4 text-sm">
                              <div className="text-right">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Venta Total</div>
                                <div className="font-bold text-gray-800 dark:text-gray-200">
                                  Bs. {fmtBs(cliente.total_venta_bs)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Cobrado</div>
                                <div className="font-bold text-green-600 dark:text-green-400">
                                  Bs. {fmtBs(cliente.total_cobrado_bs)}
                                </div>
                              </div>
                              {Number(cliente.total_deuda_bs || 0) > 0 && (
                                <div className="text-right">
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Deuda</div>
                                  <div className="font-bold text-red-600 dark:text-red-400">
                                    Bs. {fmtBs(cliente.total_deuda_bs)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Productos del cliente */}
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cliente.productos.map((prod) => (
                              <div
                                key={prod.producto_id}
                                className="border dark:border-gray-700 rounded-lg p-3"
                              >
                                <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                  {prod.producto_nombre}
                                </div>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Cantidad:</span>
                                    <span className="font-semibold">{fmtInt(prod.cantidad_total)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Venta:</span>
                                    <span className="font-semibold">Bs. {fmtBs(prod.venta_total_bs)}</span>
                                  </div>
                                  {Number(prod.efectivo_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                                      <span className="font-semibold">Bs. {fmtBs(prod.efectivo_total_bs)}</span>
                                    </div>
                                  )}
                                  {Number(prod.transferencia_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                                      <span className="font-semibold">Bs. {fmtBs(prod.transferencia_total_bs)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between pt-1 border-t dark:border-gray-700">
                                    <span className="text-gray-600 dark:text-gray-400">Cobrado:</span>
                                    <span className="font-semibold text-green-600 dark:text-green-400">
                                      Bs. {fmtBs(prod.cobrado_total_bs)}
                                    </span>
                                  </div>
                                  {Number(prod.deuda_total_bs || 0) > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Deuda:</span>
                                      <span className="font-semibold text-red-600 dark:text-red-400">
                                        Bs. {fmtBs(prod.deuda_total_bs)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Totales del cliente */}
                          <div className="mt-3 pt-3 border-t dark:border-gray-700 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Cantidad total:</span>
                              <div className="font-semibold">{fmtInt(cliente.total_cantidad)}</div>
                            </div>
                            {Number(cliente.total_efectivo_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Efectivo:</span>
                                <div className="font-semibold">Bs. {fmtBs(cliente.total_efectivo_bs)}</div>
                              </div>
                            )}
                            {Number(cliente.total_transferencia_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Transferencia:</span>
                                <div className="font-semibold">Bs. {fmtBs(cliente.total_transferencia_bs)}</div>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Cobrado:</span>
                              <div className="font-semibold text-green-600 dark:text-green-400">
                                Bs. {fmtBs(cliente.total_cobrado_bs)}
                              </div>
                            </div>
                            {Number(cliente.total_deuda_bs || 0) > 0 && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Deuda:</span>
                                <div className="font-semibold text-red-600 dark:text-red-400">
                                  Bs. {fmtBs(cliente.total_deuda_bs)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Section>
  );
};